// Copy for /how-it-works (Stratodata Stairway + 3-step process).
// Stairway step names are shared with other pages — keep them consistent.

const en = {
  seoTitle: 'How It Works | Stratodata',
  seoDescription: 'Three steps from scattered data to an AI that runs your numbers: connect your data, see your North Star, then automate and talk to it.',
  hero: {
    eyebrow: 'How It Works',
    title: 'Three steps to an AI that runs your numbers',
    subtitle: 'Every retailer we work with starts on the same six-step staircase — most are stuck around step two. Here’s exactly what changes once Stratodata connects your data, and how little of it is on you.',
    ctaPrimary: 'Book a 30-min call',
    ctaSecondary: 'See the 3 steps',
  },
  stairway: {
    eyebrow: 'Where most retailers get stuck',
    title: 'The Stratodata Stairway — six levels of data maturity',
    youAreHere: 'You are likely here',
    destination: 'The destination',
    levels: [
      { title: 'Excel Age', detail: 'Numbers copied by hand between spreadsheets. No single source of truth.' },
      { title: 'Data Silos', detail: 'Every platform collects data. Ask “what was yesterday’s revenue?” and get five answers.' },
      { title: 'Single Source of Truth', detail: 'One warehouse, one set of reports. This is where Step 1 below begins.' },
      { title: 'Data Culture', detail: 'Teams read the numbers and act on them. Still manual, but decisions are grounded.' },
      { title: 'Scaled Automation', detail: 'Pricing, bidding and forecasting run themselves. Step 3 below.' },
      { title: 'AI-Powered', detail: '“Talk to your data” — natural language in, an accurate answer out.' },
    ],
    noteBefore: 'Stratodata exists to move you from wherever you sit today straight to ',
    noteHighlight: 'level 6',
    noteAfter: ' — without asking you to build a data team of your own along the way. The three steps below are how.',
  },
  process: {
    eyebrow: 'The process',
    title: 'What actually happens after you sign up',
    youLabel: 'You',
    usLabel: 'Stratodata',
    steps: [
      {
        tag: 'Connect',
        title: 'We build your Unified Retail Data Model.',
        description: "ERP, e-commerce, marketing platforms and warehouse feeds — connected and blended into one verified retail data model, refreshed daily. We know which sources to pull and how to avoid “garbage in, garbage out,” because we've done it dozens of times.",
        you: 'Grant read access to your existing platforms.',
        us: 'Data engineers handle every pipeline and feed — nothing to export or map yourself.',
      },
      {
        tag: 'See',
        title: 'Your North Star, tracked automatically.',
        description: 'Reporting built around Net Profit — not vanity revenue — viewed by sales channel, marketing channel, product, category or brand. One number the whole company optimises for, instead of departments pulling in different directions.',
        you: 'Tell us which North Star view matters to your leadership team.',
        us: 'Consultants align the reporting — and your team — around it.',
      },
      {
        tag: 'Automate & talk',
        title: 'Rules run on their own — and you can ask questions.',
        description: 'Autopricing, PPC bid enrichment, product recommenders and demand forecasting run continuously against your data model. On top, a chat interface — grounded in your own business glossary — answers questions in plain language, in the tools you already use.',
        you: 'Set the guardrails: margin floors, stock rules, aggressiveness.',
        us: 'We build and tune the automations, and wire up “talk to your data.”',
      },
    ],
    browsePrompt: 'Want to see each piece on its own first?',
    browseLink: 'Browse our solutions',
  },
  talk: {
    eyebrow: 'Step 03, in practice',
    title: 'No more scrolling reports to find the one number you need',
    text: 'Ask in Slack, Teams or Google Chat. The question is passed to an LLM trained on your business logic, which pulls the answer straight from your data warehouse — as text, a chart, or an .xlsx export.',
    askLabel: 'ask >',
    question: '“What’s holding me back from my Net Profit target this month?”',
    flowQuery: 'chat query',
    flowLlm: 'LLM + business glossary',
    flowWarehouse: 'data warehouse',
    getLabel: 'get  >',
    answer: 'Category “Outdoor” margin is down 4pts — driven by a pricing rule change on Oct 3.',
  },
  proof: {
    eyebrow: 'What it looks like once it’s running',
    title: 'Retailers who’ve made the climb',
    items: [
      { num: '4×', label: 'revenue growth — Top4Running' },
      { num: '+40%', label: 'growth — Alasans' },
      { num: '2×', label: 'subscriptions — HeroHero' },
      { num: '5M+', label: 'SKUs managed daily' },
      { num: '€3B+', label: 'in reported marketing budgets' },
    ],
  },
  cta: {
    title: 'Ready to see it on your own data?',
    text: 'Thirty minutes, no deck. Bring your messiest report and we’ll show you what level 6 looks like for it.',
    button: 'Book a 30-min call',
  },
};

const sk: typeof en = {
  seoTitle: 'Ako to funguje | Stratodata',
  seoDescription: 'Tri kroky od roztrúsených dát k AI, ktorá sa stará o vaše čísla. Prepojíte dáta, sledujete North Star metriku a potom všetko automatizujete a pýtate sa dát priamo.',
  hero: {
    eyebrow: 'Ako to funguje',
    title: 'Tri kroky k AI, ktorá sa stará o vaše čísla',
    subtitle: 'Každý obchodník, s ktorým pracujeme, začína na rovnakom schodisku so šiestimi schodmi. Väčšina z nich uviazla niekde pri druhom. Pozrite sa, čo presne sa zmení, keď Stratodata prepojí vaše dáta, a ako málo práce pri tom zostane na vás.',
    ctaPrimary: 'Rezervovať 30-minútový hovor',
    ctaSecondary: 'Pozrieť 3 kroky',
  },
  stairway: {
    eyebrow: 'Kde sa väčšina obchodníkov zasekne',
    title: 'Stratodata Stairway – šesť úrovní dátovej zrelosti',
    youAreHere: 'Tu ste pravdepodobne vy',
    destination: 'Cieľ',
    levels: [
      { title: 'Éra Excelu', detail: 'Čísla sa ručne prepisujú z tabuľky do tabuľky. Jeden zdroj pravdy chýba.' },
      { title: 'Dátové silá', detail: 'Každá platforma zbiera dáta. Opýtate sa „aké boli včerajšie tržby?“ a dostanete päť odpovedí.' },
      { title: 'Jeden zdroj pravdy', detail: 'Jeden dátový sklad, jedna sada reportov. Tu začína krok 1 nižšie.' },
      { title: 'Dátová kultúra', detail: 'Tímy čítajú čísla a riadia sa nimi. Stále ručne, ale už sa rozhodujú podľa dát.' },
      { title: 'Automatizácia vo veľkom', detail: 'Ceny, bidy aj predikcie sa riadia samé. To je krok 3 nižšie.' },
      { title: 'Firma poháňaná AI', detail: 'Rozprávate sa so svojimi dátami. Spýtate sa bežnou rečou a dostanete presnú odpoveď.' },
    ],
    noteBefore: 'Stratodata vás chce z miesta, kde ste dnes, dostať rovno na ',
    noteHighlight: 'úroveň 6',
    noteAfter: ', a to bez toho, aby ste si museli budovať vlastný dátový tím. Ako to urobíme, ukazujú tri kroky nižšie.',
  },
  process: {
    eyebrow: 'Postup',
    title: 'Čo sa stane, keď začneme spolupracovať',
    youLabel: 'Vy',
    usLabel: 'Stratodata',
    steps: [
      {
        tag: 'Prepojiť',
        title: 'Postavíme vám jednotný dátový model pre retail.',
        description: 'ERP, e-shop, marketingové platformy a dáta zo skladu prepojíme do jedného overeného dátového modelu pre retail, ktorý sa aktualizuje každý deň. Robili sme to už desiatky ráz, takže vieme, ktoré zdroje napojiť a ako sa vyhnúť pravidlu „aké dáta dnu, také výsledky von“.',
        you: 'Dáte nám prístup na čítanie k platformám, ktoré už používate.',
        us: 'Dátoví inžinieri sa postarajú o všetky dátové toky a feedy. Vy nič neexportujete ani nemapujete.',
      },
      {
        tag: 'Vidieť',
        title: 'North Star metriku sledujeme automaticky.',
        description: 'Reporting stavia na čistom zisku, nie na tržbách, ktoré len pekne vyzerajú. Vidíte ho podľa predajného a marketingového kanála, produktu, kategórie či značky. Celá firma tak ťahá za jedno číslo a oddelenia sa prestanú rozchádzať.',
        you: 'Poviete nám, ktorý pohľad na North Star metriku zaujíma vaše vedenie.',
        us: 'Konzultanti podľa neho nastavia reporting a zladia s ním aj váš tím.',
      },
      {
        tag: 'Automatizovať a pýtať sa',
        title: 'Pravidlá bežia samé – a vy sa môžete pýtať.',
        description: 'Automatická cenotvorba, obohacovanie PPC biddingu, produktové odporúčania a predikcia dopytu bežia nepretržite nad vaším dátovým modelom. K tomu dostanete chat, ktorý pozná váš firemný slovník a odpovedá bežnou rečou priamo v nástrojoch, ktoré už používate.',
        you: 'Nastavíte mantinely: minimálne marže, pravidlá pre zásoby, mieru agresivity.',
        us: 'Automatizácie postavíme, doladíme a zapojíme aj „rozhovor s dátami“.',
      },
    ],
    browsePrompt: 'Chcete si najprv pozrieť jednotlivé riešenia zvlášť?',
    browseLink: 'Prezrite si naše riešenia',
  },
  talk: {
    eyebrow: 'Krok 03 v praxi',
    title: 'Už nemusíte prechádzať reporty, aby ste našli jedno číslo',
    text: 'Opýtajte sa v Slacku, Teams alebo Google Chate. Otázku spracuje LLM natrénované na logike vášho biznisu a odpoveď vytiahne priamo z dátového skladu. Dostanete ju ako text, graf alebo export do .xlsx.',
    askLabel: 'otázka >',
    question: '„Čo mi tento mesiac bráni dosiahnuť cieľ čistého zisku?“',
    flowQuery: 'otázka v chate',
    flowLlm: 'LLM + firemný slovník',
    flowWarehouse: 'dátový sklad',
    getLabel: 'odpoveď >',
    answer: 'Marža kategórie „Outdoor“ klesla o 4 p. b. Môže za to zmena cenového pravidla z 3. októbra.',
  },
  proof: {
    eyebrow: 'Ako to vyzerá, keď to beží',
    title: 'Obchodníci, ktorí to dotiahli vyššie',
    items: [
      { num: '4×', label: 'rast tržieb (Top4Running)' },
      { num: '+40 %', label: 'rast (Alasans)' },
      { num: '2×', label: 'viac predplatných (HeroHero)' },
      { num: '5 mil.+', label: 'SKU spravovaných denne' },
      { num: '3 mld. €+', label: 'vo vykazovaných marketingových rozpočtoch' },
    ],
  },
  cta: {
    title: 'Chcete to vidieť na vlastných dátach?',
    text: 'Tridsať minút, bez prezentácie. Prineste svoj najzamotanejší report a ukážeme vám, ako by preň vyzerala úroveň 6.',
    button: 'Rezervovať 30-minútový hovor',
  },
};

export const howItWorksCopy = { en, sk };
